import { Linking, Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';

import type { SupportedLocale } from '../hooks/useLocale';

const DEFAULT_MARKETING_BASE = 'https://tabbio.com';

export const GOOGLE_OUTDATED_CONTENT_URL =
  'https://search.google.com/search-console/remove-outdated-content';

function getMarketingBaseUrl() {
  const env = process.env.EXPO_PUBLIC_SITE_URL?.trim();
  if (!env) {
    return DEFAULT_MARKETING_BASE;
  }
  const normalized = env.startsWith('http://') || env.startsWith('https://')
    ? env
    : `https://${env}`;
  return normalized.replace(/\/+$/, '');
}

export type MarketingLink = 'privacy' | 'terms' | 'help' | 'accountDeletion';

const PATHS: Record<MarketingLink, string> = {
  privacy: '/privacy-policy',
  terms: '/terms-of-service',
  accountDeletion: '/account-deletion',
  help: '/faq',
};

export function buildMarketingUrl(
  link: MarketingLink,
  locale: SupportedLocale = 'en',
) {
  return `${getMarketingBaseUrl()}/${locale}${PATHS[link]}`;
}

let pendingOpen: Promise<void> | null = null;

async function openExternalUrl(url: string) {
  if (pendingOpen) {
    return pendingOpen;
  }

  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    return;
  }

  pendingOpen = (async () => {
    try {
      await WebBrowser.openBrowserAsync(url);
    } catch {
      await Linking.openURL(url).catch(() => undefined);
    }
  })().finally(() => {
    pendingOpen = null;
  });

  return pendingOpen;
}

export async function openMarketingLink(
  link: MarketingLink,
  locale: SupportedLocale = 'en',
) {
  const url = buildMarketingUrl(link, locale);
  return openExternalUrl(url);
}

export function openGoogleOutdatedContentTool() {
  return openExternalUrl(GOOGLE_OUTDATED_CONTENT_URL);
}

export function openAccountGuide(topic: 'privacy' | 'data-management', locale: SupportedLocale = 'en') {
  return openExternalUrl(`https://docs.tabbio.com/${locale}/account/${topic}`);
}
